import React, { useState, useEffect } from "react";
import Confetti from "react-confetti";
import { Fireworks } from "@fireworks-js/react";
import { motion } from "framer-motion";
import { Fingerprint, Scissors } from "lucide-react";

const LibraryInaguration = () => {
  const [started, setStarted] = useState(false);
  const [fadeOut, setFadeOut] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [showRibbon, setShowRibbon] = useState(false);
  const [windowSize, setWindowSize] = useState({ width: 0, height: 0 });
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isScanning, setIsScanning] = useState(false);
  const [showRibbonConfetti, setShowRibbonConfetti] = useState(false);
  const [isRibbonStretching, setIsRibbonStretching] = useState(false);
  const [isRibbonCut, setIsRibbonCut] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false); // NEW


const line1 = "بِسْمِ ٱللَّٰهِ ٱلرَّحْمَٰنِ ٱلرَّحِيمِ";
// Instead of text, make it a JSX element with your logo
const line2 = (
  <div className="logo-line">
    <img src="/assets/img/logo/new-logo.png" alt="Logo" />
  </div>
);
const line3 = "Digital Library";
const line4 = "Inaugurated By";
const line5 = "Major General Wajid Aziz";
const line6 = "HI(M)";
const line7 = "on 23rd October, 2025";
  const [typedLines, setTypedLines] = useState(["", "", "", "", "", "", ""]);

  useEffect(() => {
    setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const handleStart = () => {
    if (isScanning) return;
    setIsScanning(true);

    setTimeout(() => {
      setIsScanning(false);
      setIsTransitioning(true);
      setStarted(true);
      setTimeout(() => {
        setShowCelebration(true);
      }, 3400);
    }, 2000);
  };



   const formatTime = (date) =>
    date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });

  const formatDate = (date) =>
    date.toLocaleDateString([], { weekday: "long", year: "numeric", month: "long", day: "numeric" });

  useEffect(() => {
  const vid = document.querySelector(".bg-video");
  if (vid) vid.playbackRate = 0.9;
}, []);

   useEffect(() => {
    if (started && showCelebration) {
      const typeLine = async (text, index) => {
        setTypedLines((prev) => {
          const copy = [...prev];
          copy[index] = "";
          return copy;
        });
        for (let i = 0; i < text.length; i++) {
          await new Promise((resolve) => setTimeout(resolve, 80));
          setTypedLines((prev) => {
            const copy = [...prev];
            copy[index] += text.charAt(i);
            return copy;
          });
        }
      };

    const runTyping = async () => {
  await typeLine(line1, 0);

  // Directly show logo without typing
  setTypedLines((prev) => {
    const copy = [...prev];
    copy[1] = line2; // JSX element
    return copy;
  });

  await typeLine(line3, 2);
  await typeLine(line4, 3);
  await typeLine(line5, 4);
  await typeLine(line6, 5);
  await typeLine(line7, 6);
};


      runTyping();
    }
  }, [started, showCelebration]);

  return (
    <>
      <style>{`
      .inauguration-container {
  position: relative;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  font-family: 'Segoe UI', sans-serif;
}

/* === Library + Celebration Videos === */
.bg-video {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 10;
  transition: opacity 2s ease-in-out;
  z-index: 0;
  filter: brightness(0.95) saturate(1.1);
}

/* Overlay for all bg videos */
.bg-video::after {
  content: "";
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.35); /* lighter black overlay */
  z-index: 1;
  pointer-events: none;
}

/* Normal visible video */
.bg-video.visible {
  opacity: 1;
}

/* Fading out transition */
.bg-video.fade-out {
  opacity: 0.4;
}

/* === Realistic Green Curtain (Velvet Style) === */
.curtain-wrapper {
  position: absolute;
  inset: 0;
  display: flex;
  z-index: 10;
  overflow: hidden;
  pointer-events: none;
}

/* Both Curtains */
.curtain {
  flex: 1;
  height: 100%;
  position: relative;
  background:
  repeating-linear-gradient(
    90deg,
    #063f43 0%,     /* Deep shadow teal */
    #0a9d9f 3%,     /* Main theme color */
    #0d7d84 6%,     /* Slightly darker mid fold */
    #0fb5b9 9%,     /* Highlight ridge */
    #063f43 12%     /* Return to dark shadow */
  );
  background-size: 200% 100%;
  box-shadow: inset -20px 0 30px rgba(0, 0, 0, 0.6),
              inset 20px 0 30px rgba(0, 0, 0, 0.6);
  transform-origin: left center;
  animation: openCurtains 4s cubic-bezier(0.77, 0, 0.175, 1) forwards;
  filter: brightness(1);
  transition: filter 1s ease-in-out;
}

/* Left Curtain */
.curtain.left-curtain {
  transform-origin: left center;
  border-right: 2px solid rgba(0, 0, 0, 0.3);
}

/* Right Curtain */
.curtain.right-curtain {
  transform-origin: right center;
  border-left: 2px solid rgba(0, 0, 0, 0.3);
}

/* Opening animation */
@keyframes openCurtains {
  0% {
    transform: scaleX(1);
    opacity: 2;
  }
  60% {
    transform: scaleX(0.7);
  }
  100% {
    transform: scaleX(0);
    opacity: 1;
    visibility: hidden;
  }
}

/* Subtle fabric motion (adds realism without brightness) */
.curtain::before {
  content: "";
  position: absolute;
  inset: 0;
  background: repeating-linear-gradient(
    90deg,
    rgba(0, 0, 0, 0.25) 0%,
    rgba(0, 0, 0, 0.1) 3%,
    rgba(0, 0, 0, 0.25) 6%
  );
  mix-blend-mode: multiply;
  opacity: 0.5;
  animation: fabricMotion 6s ease-in-out infinite alternate;
}

@keyframes fabricMotion {
  0% { background-position: 0 0; }
  100% { background-position: 20px 0; }
}


/* === Celebration video motion === */
.bg-video.celebration {
  z-index: 5;
  animation: slowZoom 20s ease-in-out infinite alternate;
}

@keyframes slowZoom {
  from { transform: scale(1); }
  to { transform: scale(1.06); }
}

/* === Overlay Text === */
.video-overlay-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  z-index: 20;
  color: white;
  font-family: 'Segoe UI', sans-serif;
}

/* Gentle zoom-in/out movement */
@keyframes slowZoom {
  from { transform: scale(1); }
  to { transform: scale(1.06); }
}

/* Fade transition between loops */
.fade-out {
  opacity: 0.6;
}

/* Optional soft overlay for readability */
.video-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    120deg,
    rgba(46,139,87,0.25),
    rgba(255,248,231,0.4),
    rgba(0,0,0,0.35)
  );
  mix-blend-mode: overlay;
  z-index: 1;
  pointer-events: none;
}

        .video-wrapper {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          overflow: hidden;
        }

   .bg-video {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;

 
}

/* Gradient fingerprint scan line */
.scan-line {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 4px;
  background: linear-gradient(90deg, #e6f1e6ff, white, #e5f1e5ff);
  animation: scanMove 1.2s linear infinite;
}
@keyframes scanMove {
  0% { transform: translateY(0); }
  100% { transform: translateY(64px); } /* matches fingerprint size */
}

/* Fingerprint glow matches flag theme */
.open-button.scanning .fingerprint-icon {
  animation: fingerprintGlow 1.5s ease-in-out infinite;
}
@keyframes fingerprintGlow {
  0%, 100% {
    filter: drop-shadow(0 0 6px #ecf1efff);
    transform: scale(1);
  }
  50% {
    filter: drop-shadow(0 0 16px white);
    transform: scale(1.08);
  }
}



        .video-green-overlay {
          position: absolute;
          inset: 0;
          background: rgba(0, 128, 0, 0.45);
          mix-blend-mode: multiply;
          z-index: 1;
          pointer-events: none;
        }
        .button-overlay {
          position: absolute;
          inset: 0;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          z-index: 20;
          text-align: center;
          transition: opacity 1s ease;
        }
        .fade-out {
          opacity: 0;
          pointer-events: none;
        }
        
        .logo {
          max-width: 140px;
          margin-bottom: 2rem;
          filter: drop-shadow(0 0 25px rgba(255,255,255,0.4));
        }
        .tagline {
          font-size: 1.6rem;
          font-weight: 600;
          color: #FFD700;
          margin-bottom: 2rem;
          text-shadow: 0 0 10px rgba(255,215,0,0.8);
        }
        .open-button {
          position: relative;
          padding: 1.5rem 3.5rem;
          font-size: 2rem;
          font-weight: bold;
          text-transform: uppercase;
          color: white;
          border: none;
          border-radius: 9999px;
          background: linear-gradient(135deg, #0a9d9f, #0a9d9f, #0a9d9f);
          cursor: pointer;
          overflow: hidden;
          box-shadow: 0 0 60px rgba(255, 255, 255, 0.2);
        }
        .open-button:hover {
          transform: scale(1.08);
          box-shadow: 0 0 90px rgba(255, 255, 255, 0.4);
        }
        .fingerprint-icon {
          stroke: white;
        }
        .open-button.scanning .fingerprint-icon {
          animation: fingerprintGlow 1.5s ease-in-out infinite;
        }
        @keyframes fingerprintGlow {
          0%, 100% {
            filter: drop-shadow(0 0 5px #e2ebe6ff);
            transform: scale(1);
          }
          50% {
            filter: drop-shadow(0 0 15px #d9e7e1ff);
            transform: scale(1.1);
          }
        }
        .scan-line {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 8px;
          background: rgba(243, 243, 243, 0.7);
          border-radius: 4px;
          animation: scanLineDown 1.5s ease-in-out forwards;
        }
        @keyframes scanLineDown {
          0% { top: 0%; opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { top: 100%; opacity: 0; }
        }
        .video-overlay-text {
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          text-align: center;
          z-index: 25;
        }

     .event-line.first-line {
  margin-bottom: 1.0em; /* bigger space after Bismillah */
  font-size: 3.0rem;
  font-weight: 700;
  color: #006400; /* dark green for white background */
  text-shadow: none; /* remove glow for better readability */
}

.event-line.first-line {
  margin-bottom: 1em;
  font-size: 3rem;
  font-weight: 700;
  color: #100f0f;
  text-shadow: 0 0 6px #141414, 0 0 0px rgb(20 18 18);
}

.logo-line {
  text-align: center;
  margin-bottom: 2em; /* Space after logo block */
}

.logo-line img {
  width: 230px;  /* Increased from 240px to 320px */
  height: auto;
  display: inline-block;
}


.event-line {
  margin-bottom: 1em;
  font-size: 3.0rem;
  font-weight: 700;
  background: white;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.guest-line {
  text-transform: uppercase;
  font-size: 3.5rem;
  font-weight: 800;
  background: linear-gradient(90deg, #FF5722, #fbf9fcff, #2196F3);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}



.event-dot {
  color: #ffffffff;
  font-size: 3.0rem;
  margin-bottom: 1em;
  margin-top:1em;
}

.team-line {
  margin-bottom: 1em;
  font-size: 3rem;
   background: linear-gradient(90deg, #FF5722, #fbf9fcff, #2196F3);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight:bold;
}



        .clock {
          position: absolute;
          top: 20px;
          right: 30px;
          color: white;
          font-size: 1.1rem;
          font-weight: 500;
          z-index: 50;
        }
        .watermark {
          position: absolute;
          bottom: 10px;
          right: 15px;
          font-size: 0.85rem;
          color: rgba(255, 255, 255, 0.6);
          z-index: 25;
        }

 .ceremony-header {
  text-align: center;
  padding: 2em 1em;
}

.logo {
  width: 400px; /* bigger logo */
  height: auto;
  margin-bottom: 1em;

}

.inauguration-text {
  text-transform: uppercase;
  font-size: 3.4rem;
  font-weight: bold;
  background: linear-gradient(90deg,rgba(10, 157, 159, 1),rgb(10, 157, 159),rgb(10, 157, 159));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: goldShimmer 3s linear infinite;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.10);
  margin-bottom: 0.6em;
}

.main-title {
  font-size: 2.5rem;
  color:rgb(116, 240, 240);
  text-shadow: 2px 2px 5px rgba(0,0,0,0.4);
}

.main-subtitle {
  font-size: 2.8rem;
  font-weight: 900;

  color:rgb(31, 236, 236);
  text-shadow: 1px 1px 3px rgba(0,0,0,0.3);
}

@keyframes goldShimmer {
  0% { background-position: 0% center; }
  100% { background-position: 200% center; }
}

.video-wrapper {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  z-index: 0;
}

.video-black-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.50); /* soft black overlay */
  z-index: 1;
  pointer-events: none;
  transition: opacity 2s ease-in-out;
}

.video-black-overlays {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.50); /* soft black overlay */
  z-index: 1;
  pointer-events: none;
  transition: opacity 2s ease-in-out;
}



.bg-video {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
  filter: brightness(0.95) saturate(1.1);
}

/* Smooth fade when library fades out */
.bg-video.fade-out + .video-black-overlay {
  opacity: 0.2;
}
.video-wrap {
  position: relative;
  width: 100%;
  height: 100vh; /* or your desired height */
  overflow: hidden;
}

.celebration-img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;   /* ensures it fills area nicely */
  display: block;
  z-index: 0;
}

.video-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.25); /* soft dark overlay */
  z-index: 1;
  pointer-events: none;
}



      `}</style>


       <div className="inauguration-container">
        {/* <div className="clock">
          {formatDate(currentTime)} <br />
          {formatTime(currentTime)}
        </div> */}
 
{/* 1️⃣ Background library video */}
<div className="video-wrapper">
  <video
    autoPlay
    loop
    muted
    playsInline
    className={`bg-video ${!started ? "visible" : "fade-out"}`}
  >
    <source src="/assets/video/library-bg.mp4" type="video/mp4" />
  </video>
  <div className="video-black-overlay"></div>
</div>

{/* 2️⃣ Animated Green Curtains */}
{started && !showCelebration && (
  <div className="curtain-wrapper">
    <div className="curtain left-curtain"></div>
    <div className="curtain right-curtain"></div>
  </div>
)}

{/* 3️⃣ Celebration video */}
{/* 3️⃣ Celebration image */}
{showCelebration && (
  <div className="video-wrap">
    <motion.img
      src="/assets/img/opening-img.jpeg"
      alt="Celebration Background"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 2.5, ease: "easeInOut" }}
      className="celebration-img"
    />

    <div className="video-overlay" />
  </div>
)}



{/* 4️⃣ Fireworks + Text */}
{showCelebration && (
  <>
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 3, ease: "easeOut", delay: 1 }}
    >
      <Fireworks
        options={{
          speed: 3,
          particles: 120,
          trace: 4,
          explosion: 6,
          autoresize: true,
          brightness: { min: 55, max: 85 },
        }}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          zIndex: 12,
        }}
      />
    </motion.div>

    <motion.div
      className="video-overlay-text"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 2 }}
    >
      <div className="event-line first-line">
        {typedLines[0]}
      </div>
      <div className="logo-line">
        {typedLines[1]}
      </div>
      <div className="event-dot">{typedLines[2]}</div>
      <div className="event-line">{typedLines[3]}</div>
      <div className="guest-line">{typedLines[4]}</div>
      <div className="team-line">{typedLines[5]}</div>
      <div className="team-line">{typedLines[6]}</div>
    </motion.div>
  </>
)}


 {!started && !isTransitioning && (
  <motion.div
    className={`button-overlay ${fadeOut ? "fade-out" : ""}`}
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 1 }}
  >
    <div className="flag-bg"></div>
    <div className="green-overlay"></div>
    <div className="particle-layer"></div>


<div class="ceremony-header">

  <img src="../assets/img/logo/new-logo.png" alt="Logo" class="logo" />

  <div class="inauguration-text">Inauguration</div>

  <div class="main-title">Of New</div>
  <div class="main-subtitle">Digital Library</div>
  
</div>






    {/* Fingerprint button */}
    <motion.button
      className={`open-button ${isScanning ? "scanning" : ""}`}
      onClick={handleStart}
      onTouchStart={handleStart}
      aria-label="Touch fingerprint to open"
      whileTap={{ scale: 1.9 }}
    >
      <div className="fingerprint-container">
        <Fingerprint className="fingerprint-icon" size={54} strokeWidth={1.2} />
        {isScanning && <div className="scan-line" />}
      </div>
    </motion.button>
  </motion.div>
)}





        {/* Watermark */}
        {/* <div className="watermark">
          © 2025 Rawalpindi Institute of Cardiology, Rawalpindi
        </div> */}
      </div>
    </>
  );
};

export default LibraryInaguration;
